// src/components/PurchaseFlow/Steps/CustomerHandoffStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserCog, 
  faArrowRight,
  faLink,
  faCopy,
  faRefresh,
  faTimes,
  faCheck,
  faClock,
  faSpinner,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import QRCode from 'qrcode';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../../services/api';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './CustomerHandoffStep.css';

const MySwal = withReactContent(Swal);

const CustomerHandoffStep = ({ onNext, clientData, purchaseRequestId, formData }) => {

  // Configuración desde variables de entorno
  const ALLOW_PROCESSED_STEPS = process.env.REACT_APP_CUSTOMER_ALLOW_PROCESSED_STEPS;
  const ALLOWED_PROCESSED_STEPS_ARRAY = ALLOW_PROCESSED_STEPS 
    ? ALLOW_PROCESSED_STEPS.split(',').map(step => step.trim())
    : [];

  const FLOW_STEPS = process.env.REACT_APP_CUSTOMER_FLOW_STEPS.split(',').map(step => step.trim());

  // Estados
  const [customerUrl, setCustomerUrl] = useState('');
  const [flowId, setFlowId] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [customerSteps, setCustomerSteps] = useState([]);
  const [stepStatuses, setStepStatuses] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Estados para consultas manuales
  const [manualQueryCooldown, setManualQueryCooldown] = useState(0);
  const [canManualQuery, setCanManualQuery] = useState(true);
  
  // Estados de control
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const [isFlowCompleted, setIsFlowCompleted] = useState(false);
  
  // Generar token temporal y URL para QR
  useEffect(() => {
    const generateTemporaryToken = async () => {
      setIsLoadingToken(true);
      try {
        // Los pasos se configuran dinámicamente desde la respuesta de la API
        const response = await api.post(`/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/self-service/init`, {
          flow_steps: FLOW_STEPS // Pasos por defecto para la inicialización
        });
        
        const { flow_id, customer_token, expires_at } = response.data.data;
        setFlowId(flow_id);
        
        // Construir URL del cliente usando el token
        const baseUrl = window.location.origin;
        const customerFlowUrl = `${baseUrl}/m/onboarding/${customer_token}`;
        setCustomerUrl(customerFlowUrl);
        
        // Calcular tiempo restante
        if (expires_at) {
          const expiresDate = new Date(expires_at);
          const now = new Date();
          const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
          setTimeRemaining(remaining);
        }
        
        
      } catch (error) {
        console.error('Error al generar token temporal:', error);
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo iniciar el flujo de autogestión'
        });
      } finally {
        setIsLoadingToken(false);
      }
    };

    // Solo generar token si el flujo no está completado
    if (clientData?.id && purchaseRequestId && !isFlowCompleted) {
      generateTemporaryToken();
    }
  }, [clientData?.id, purchaseRequestId, isFlowCompleted]);

  // Verificar si el token ya está expirado al montar el componente
  useEffect(() => {
    if (timeRemaining <= 0 && !isLoadingToken) {
      setIsTokenExpired(true);
    }
  }, [timeRemaining, isLoadingToken]);

  // Timer para el countdown
  useEffect(() => {
    // No ejecutar el timer si el flujo está completado
    if (isFlowCompleted || timeRemaining <= 0) {
      // Si no hay tiempo restante, marcar como expirado inmediatamente
      if (timeRemaining <= 0) {
        setIsTokenExpired(true);
      }
      return;
    }
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsTokenExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, isFlowCompleted]);

  // Timer para el cooldown de consultas manuales
  useEffect(() => {
    if (manualQueryCooldown <= 0) {
      setCanManualQuery(true);
      return;
    }
    
    const timer = setInterval(() => {
      setManualQueryCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanManualQuery(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [manualQueryCooldown]);

  // Generar QR cuando el canvas esté disponible
  useEffect(() => {
    if (customerUrl && !isLoadingToken) {
      // Función para verificar si el canvas está disponible y generar el QR
      const generateQRWhenReady = () => {
        const canvas = document.getElementById('customer-qr-canvas');
        if (canvas) {
          generateQRCode(customerUrl);
        } else {
          // Si el canvas no está disponible, reintentar en 50ms
          setTimeout(generateQRWhenReady, 50);
        }
      };
      
      // Iniciar el proceso de generación del QR
      generateQRWhenReady();
    }
  }, [customerUrl, isLoadingToken]);

  const generateQRCode = async (url) => {
    try {
      const canvas = document.getElementById('customer-qr-canvas');
      if (canvas) {
        await QRCode.toCanvas(canvas, url, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
      }
    } catch (error) {
      console.error('Error generando QR:', error);
    }
  };

  // Función para verificar el progreso del cliente
  const checkCustomerProgress = async () => {
    try {
      const response = await api.get(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/self-service/status`
      );
      
      const { status, steps, expires_at, flow_config } = response.data.data;
      
      if (expires_at) {
        const expiresDate = new Date(expires_at);
        const now = new Date();
        const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
        setTimeRemaining(remaining);
        
        // Resetear el estado de expiración si el token aún es válido
        if (remaining > 0) {
          setIsTokenExpired(false);
        } else {
          setIsTokenExpired(true);
        }
      }
      
      // Configurar los pasos desde la respuesta de la API
      if (flow_config && flow_config.length > 0) {
        setCustomerSteps(flow_config);
      }
      
      if (steps) {
        setStepStatuses(steps);
      }
      
      // Si el flujo está completado, marcar como completado
      if (status === 'completed') {
        setIsFlowCompleted(true);
      }
      
    } catch (error) {
      console.error('Error verificando progreso del cliente:', error);
    }
  };

  // Función para regenerar el token temporal
  const handleRegenerateToken = async () => {
    // No permitir regenerar token si el flujo está completado
    if (isFlowCompleted) {
      MySwal.fire({
        icon: 'info',
        title: 'Flujo Completado',
        text: 'El flujo ya está completado. No se requiere regenerar el token.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    try {
      setIsRefreshing(true);
      
      const response = await api.post(
        `/customers/${clientData.id}/purchase-requests/${purchaseRequestId}/self-service/regenerate-token`
      );
      
      if (response.data.success) {
        const { customer_token, expires_at } = response.data.data;
        
        // Actualizar la URL del cliente
        const baseUrl = window.location.origin;
        const newCustomerFlowUrl = `${baseUrl}/m/onboarding/${customer_token}`;
        setCustomerUrl(newCustomerFlowUrl);
        
        // Actualizar el tiempo restante
        if (expires_at) {
          const expiresDate = new Date(expires_at);
          const now = new Date();
          const remaining = Math.max(0, Math.floor((expiresDate - now) / 1000));
          setTimeRemaining(remaining);
        }
        
        // Resetear estado de expiración
        setIsTokenExpired(false);
        
        // Hacer una nueva consulta del estado
        checkCustomerProgress();
        
        // Resetear el cooldown manual para permitir consultas inmediatas
        setManualQueryCooldown(0);
        setCanManualQuery(true);
        
        MySwal.fire({
          icon: 'success',
          title: 'Token regenerado',
          text: 'Se ha generado un nuevo QR para el cliente',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }
      
    } catch (error) {
      console.error('Error al regenerar token:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo regenerar el token temporal',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Consulta inicial del estado del cliente
  useEffect(() => {
    // Solo hacer consulta inicial si el flujo no está completado
    if (flowId && !isFlowCompleted) {
      // Consulta inicial inmediata
      checkCustomerProgress();
    }
  }, [flowId, purchaseRequestId, clientData?.id, isFlowCompleted]);

  // Mapeo de pasos a etiquetas legibles (igual que en CustomerFlow.js)
  const stepLabelMap = {
    'ValidaStep': 'Identidad',
    'PaymentStep': 'Medio de Pago',
    'ConfirmationStep': 'Contrato',
    'DocumentUploadStep': 'Documentación'
  };

  const getStepLabel = (stepName) => {
    return stepLabelMap[stepName] || stepName;
  };

  const getStepStatusMessage = (status, stepName) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'in_progress':
        return 'En progreso...';
      case 'error':
        return 'Error - Requiere atención';
      case 'processed':
        // Si el paso procesado está permitido, mostrarlo como completado
        if (stepName && isProcessedStepAllowed(stepName)) {
          return 'Completado';
        }
        return 'Procesado - Esperando Confirmación';
      default:
        return 'Pendiente';
    }
  };

  const handleStepInfo = (stepName, status) => {
    if (status === 'processed') {
      // Si el paso procesado está permitido, mostrar mensaje diferente
      if (isProcessedStepAllowed(stepName)) {
        MySwal.fire({
          icon: 'success',
          title: `Paso Completado`,
          text: 'Este paso ha sido completado por el cliente y está marcado como válido para continuar.',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#28a745'
        });
      } else {
        MySwal.fire({
          icon: 'info',
          title: `Importante`,
          html: `<div>
                   <div>Este paso fue procesado correctamente y está pendiente de completarse por parte del sistema.</div>
                   <hr>
                   <div style="font-size: 0.80em; color: #666; margin-top: 6px;">Esta validación puede demorar unos segundos.</div>
                 </div>`,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        });
      }
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(customerUrl);
      MySwal.fire({ 
        icon: 'success',
        title: 'Enlace copiado',
        text: 'El enlace ha sido copiado al portapapeles',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
    } catch (err) {
      console.error('Error al copiar:', err);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar el enlace'
      });
    }
  };

  const handleWhatsAppShare = () => {
    if (!customerUrl) return;

    const clientName = clientData?.firstName || clientData?.name || 'Cliente';
    // Obtener teléfono desde formData (pasado desde DataEntryStep)
    const phone = formData?.contactPhone;
    
    const whatsappText = encodeURIComponent(
      `Hola ${clientName}, completa tu proceso de préstamo desde tu celular: ${customerUrl}`
    );
    const whatsappUrl = phone ? 
      `https://wa.me/${phone}?text=${whatsappText}` : 
      `https://wa.me/?text=${whatsappText}`;
    
    window.open(whatsappUrl, '_blank');
  };

  const handleRefreshStatus = async () => {
    // No permitir consultas si el flujo está completado
    if (isFlowCompleted) {
      MySwal.fire({
        icon: 'info',
        title: 'Flujo Completado',
        text: 'El flujo ya está completado. No se requieren más consultas.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    // Verificar si puede hacer consulta manual (cooldown de 10 segundos)
    if (!canManualQuery) {
      MySwal.fire({
        icon: 'warning',
        title: 'Espera un momento',
        text: `Debes esperar ${formatTime(manualQueryCooldown)} para la próxima consulta manual`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
      return;
    }

    setIsRefreshing(true);
    setCanManualQuery(false);
    
    try {
      await checkCustomerProgress();
      
      // Configurar el cooldown de 10 segundos para la próxima consulta manual
      setManualQueryCooldown(10);
      
      // Mostrar mensaje de éxito
      MySwal.fire({
        icon: 'success',
        title: 'Estado actualizado',
        text: 'Se ha consultado el estado del cliente',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
      
    } catch (error) {
      console.error('Error al refrescar estado:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el estado del cliente',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDiscardSale = () => {
    if (window.confirm('¿Estás seguro de que deseas anular esta venta?')) {
      // Lógica de anulación
    }
  };

  const handleContinue = () => {
    onNext({});
  };

  const isProcessedStepAllowed = (stepName) => {
    return ALLOWED_PROCESSED_STEPS_ARRAY.includes(stepName);
  };

  const getCompletedStepsCount = () => {
    return Object.entries(stepStatuses).filter(([step, status]) => {
      return status === 'completed' || (status === 'processed' && isProcessedStepAllowed(step));
    }).length;
  };

  const canContinueWithProcessedSteps = () => {
    const allStepsCompleted = customerSteps.every(step => {
      const status = stepStatuses[step];
      return status === 'completed' || (status === 'processed' && isProcessedStepAllowed(step));
    });
    
    return allStepsCompleted;
  };

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-12">
          {/* Header */}
          <div className="text-center mb-4">
            <h2 className="mb-3">
              <FontAwesomeIcon icon={faUserCog} className="me-2" />
              Autogestión del Cliente
            </h2>
            <p className="lead text-muted">
            El cliente debe completar estos pasos desde su celular
            </p>
          </div>

                    <div className="row">
            {/* Columna izquierda - QR y controles */}
            {!isFlowCompleted && (
              <div className="col-lg-6">
                {/* QR Code Section */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-body text-center p-4">
                    {isLoadingToken ? (
                      <div>
                        <div className="mb-3">
                          <Skeleton 
                            width={200} 
                            height={200} 
                            className="mx-auto"
                            style={{ borderRadius: '8px' }}
                          />
                        </div>
                        
                        <div className="mb-3">
                          <Skeleton width={180} height={16} className="mx-auto" />
                        </div>
                        
                        <div className="mb-3">
                          <Skeleton width={150} height={20} className="mx-auto" />
                        </div>
                        
                        <div className="mb-3">
                          <Skeleton width={220} height={16} className="mx-auto" />
                        </div>
                        
                        <div className="mb-1">
                          <Skeleton height={38} className="w-100" />
                        </div>
                      </div>
                    ) : (
                      <>
                        {isTokenExpired ? (
                          <div className="qr-container mb-1 p-3 border rounded d-flex align-items-center justify-content-center">
                            <div className="text-center d-flex flex-column align-items-center justify-content-center">
                              <div className="mb-3">
                                <FontAwesomeIcon 
                                  icon={faClock} 
                                  className="text-light-warning" 
                                  style={{ fontSize: '3rem' }} 
                                />
                              </div>
                              <p className="text-muted small mb-3 text-center">
                                El enlace ha expirado
                              </p>
                              <button 
                                className="btn btn-light-warning btn-sm"
                                onClick={handleRegenerateToken}
                                disabled={isRefreshing}
                              >
                                <FontAwesomeIcon 
                                  icon={faRefresh} 
                                  className={isRefreshing ? 'fa-spin me-2' : 'me-2'} 
                                />
                                {isRefreshing ? 'Regenerando...' : 'Regenerar QR'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="qr-container mb-1">
                              <canvas 
                                id="customer-qr-canvas" 
                                className="border rounded"
                                style={{ maxWidth: '200px' }}
                              ></canvas>
                            </div>
                            
                            <p className="text-muted small mb-3">
                              Escanear este QR para continuar
                            </p>
                            
                            {timeRemaining > 0 && (
                              <div className="text-warning d-flex align-items-center justify-content-center mb-3 p-0">
                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                <span className="fw-bold">
                                  El link expira en {formatTime(timeRemaining)}
                                </span>
                              </div>
                            )}
                          </>
                        )}

                        <p className="text-muted small mb-3">
                          {isTokenExpired ? 'Regenera el QR para obtener un nuevo enlace' : 'También puedes: copiar el enlace o enviarlo por WhatsApp'}
                        </p>
                        
                        <div className="input-group mb-1">
                          <span className="input-group-text">
                            <FontAwesomeIcon icon={faLink} />
                          </span>
                          <input 
                            type="text" 
                            className="form-control form-control-sm" 
                            value={isTokenExpired ? '' : customerUrl}
                            readOnly
                            disabled={isTokenExpired}
                            style={isTokenExpired ? { backgroundColor: '#e9ecef', color: '#6c757d' } : {}}
                            placeholder={isTokenExpired ? '' : ''}
                          />
                          <button 
                            className="btn btn-outline-secondary" 
                            type="button"
                            onClick={handleCopyLink}
                            disabled={isTokenExpired}
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>

                          <button 
                            className="btn btn-success"
                            onClick={handleWhatsAppShare}
                            disabled={isTokenExpired}
                          >
                            <i className="bi bi-whatsapp"></i>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Columna derecha - Progreso del cliente */}
            <div className={isFlowCompleted ? "col-12" : "col-lg-6"}>
              {/* Customer Progress */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  {!isFlowCompleted && (
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">
                        <FontAwesomeIcon icon={faUserCog} className="me-2" />
                        Progreso del Cliente
                      </h5>
                    </div>
                  )}
                  
                  {isLoadingToken ? (
                    <div className="customer-progress">
                      {[1, 2, 3].map((index) => (
                        <div key={index} className="d-flex align-items-center mb-3">
                          <div className="flex-shrink-0 me-3">
                            <Skeleton 
                              width={40} 
                              height={40} 
                              circle={true}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <Skeleton width={120} height={20} className="mb-2" />
                            <Skeleton width={100} height={16} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : isFlowCompleted ? (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <FontAwesomeIcon icon={faCheck} className="text-success" style={{ fontSize: '3rem' }} />
                      </div>
                      <h5 className="text-success mb-2">¡Flujo Completado!</h5>
                      <p className="text-muted mb-0">
                        El cliente ha completado todos los pasos exitosamente.
                        Ya puedes continuar con el proceso.
                      </p>
                    </div>
                  ) : (
                    <div className="customer-progress">
                      {customerSteps.map((step, index) => {
                        const status = stepStatuses[step] || 'pending';
                        const isCompleted = status === 'completed';
                        const isInProgress = status === 'in_progress';
                        const hasError = status === 'error';
                        const isProcessed = status === 'processed';
                        
                        const isProcessedAllowed = isProcessed && isProcessedStepAllowed(step);
                        const shouldShowAsCompleted = isCompleted || isProcessedAllowed;
                        
                        return (
                          <div key={step} className="d-flex align-items-center mb-3">
                            <div className="flex-shrink-0 me-3">
                              <div 
                                className={`rounded-circle d-flex align-items-center justify-content-center ${
                                  shouldShowAsCompleted ? 'bg-light-success' : 
                                  isInProgress ? 'bg-light-primary' : 
                                  hasError ? 'bg-light-danger' : 
                                  isProcessed ? 'bg-light-warning' : 'bg-light-secondary'
                                }`}
                                style={{ 
                                  width: '2.5rem', 
                                  height: '2.5rem',
                                  backgroundColor: shouldShowAsCompleted ? '#28a745' : 
                                                 isInProgress ? 'rgba(var(--bs-primary-rgb),1)' : 
                                                 hasError ? '#dc3545' : 
                                                 isProcessed ? '#ffc107' : '#d6d6d6'
                                }}
                              >
                                {shouldShowAsCompleted ? (
                                  <FontAwesomeIcon icon={faCheck} className="text-white" />
                                ) : isInProgress ? (
                                  <FontAwesomeIcon icon={faSpinner} className="text-white fa-spin" />
                                ) : hasError ? (
                                  <FontAwesomeIcon icon={faExclamationCircle} className="text-white" />
                                ) : isProcessed ? (
                                  <FontAwesomeIcon icon={faClock} className="text-white" />
                                ) : (
                                  <span className="text-white fw-bold">{index + 1}</span>
                                )}
                              </div>
                            </div>
                            <div 
                              className={`flex-grow-1 ${isProcessed ? 'step-processed-clickable' : ''}`}
                              onClick={isProcessed ? () => handleStepInfo(step, status) : undefined}
                              style={isProcessed ? { cursor: 'pointer' } : {}}
                            >
                              <h6 className="mb-1">{getStepLabel(step)}</h6>
                              <div className="d-flex align-items-center">
                                <p className="mb-0 small text-muted me-2">
                                  {getStepStatusMessage(status, step)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {isLoadingToken ? (
                    <div className="mt-4">
                      <Skeleton height={8} className="mb-2" />
                      <Skeleton width={150} height={16} className="mx-auto" />
                    </div>
                  ) : (
                    <div className="mt-4">
                      <div className="progress mb-2" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar" 
                          style={{ 
                            width: `${(getCompletedStepsCount() / customerSteps.length) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <p className="small text-muted text-center mb-0">
                        {getCompletedStepsCount()} de {customerSteps.length} pasos completados
                      </p>
                    </div>
                  )}
                  
                  {isLoadingToken ? (
                    <div className="mt-2 p-1">
                      <div className="d-grid gap-2">
                        <Skeleton height={38} />
                        <Skeleton height={38} />
                        <Skeleton height={38} />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 p-1">
                      <div className={isFlowCompleted ? "d-flex gap-2" : "d-grid gap-2"}>
                        {/* <button 
                          type="button" 
                          className={`btn btn-outline-danger ${isFlowCompleted ? "flex-fill" : ""}`}
                          onClick={handleDiscardSale}
                          disabled={true}
                        >
                          <FontAwesomeIcon icon={faTimes} className="me-2" />
                          Descartar venta
                        </button> */}
                        {!isFlowCompleted && (
                          <button 
                            type="button" 
                            className="btn btn-outline-primary"
                            onClick={handleRefreshStatus}
                            disabled={isRefreshing || !canManualQuery || isTokenExpired}
                          >
                            <FontAwesomeIcon icon={faRefresh} className="me-2" />
                            {canManualQuery ? 'Consultar Estado' : `Consultar en ${formatTime(manualQueryCooldown)}`}
                          </button>
                        )}
                        <button 
                          type="button" 
                          className={`btn btn-primary ${isFlowCompleted ? "flex-fill" : ""}`}
                          onClick={handleContinue}
                          disabled={!canContinueWithProcessedSteps()}
                        >
                          Continuar
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CustomerHandoffStep;
